import { useState, useCallback, useEffect } from 'react';
import { useScheduleIntervals, ScheduleInterval } from './useScheduleIntervals';

export interface SchedulingAction {
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  intervals: ScheduleInterval[];
  previousState?: ScheduleInterval[];
}

export function useSchedulingActions(scheduleId: string | null, selectedDate: Date) {
  const [history, setHistory] = useState<SchedulingAction[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [copiedIntervals, setCopiedIntervals] = useState<ScheduleInterval[]>([]);

  const { data: scheduleIntervals } = useScheduleIntervals({
    scheduleId: scheduleId || undefined,
    date: selectedDate,
  });

  // Add action to history
  const addToHistory = useCallback((action: SchedulingAction) => {
    setHistory((prev) => {
      // Remove any "future" history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(action);
      // Limit history to 50 actions
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Undo last action
  const undo = useCallback(() => {
    if (historyIndex >= 0) {
      const action = history[historyIndex];
      setHistoryIndex(historyIndex - 1);
      return action;
    }
    return null;
  }, [history, historyIndex]);

  // Redo last undone action
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const action = history[historyIndex + 1];
      setHistoryIndex(historyIndex + 1);
      return action;
    }
    return null;
  }, [history, historyIndex]);

  // Copy selected intervals
  const copyIntervals = useCallback((selectedAgentIds: string[]) => {
    if (!scheduleIntervals) return;

    const intervalsToCopy = scheduleIntervals.filter((interval) =>
      selectedAgentIds.includes(interval.agentId)
    );

    setCopiedIntervals(intervalsToCopy);
    return intervalsToCopy.length;
  }, [scheduleIntervals]);

  // Paste copied intervals to selected agents
  const pasteIntervals = useCallback((targetAgentIds: string[]) => {
    if (copiedIntervals.length === 0) return null;

    // Create new intervals for each target agent based on copied intervals
    const newIntervals = targetAgentIds.flatMap((targetAgentId) =>
      copiedIntervals.map((interval) => ({
        ...interval,
        id: undefined, // Remove ID so API creates new ones
        agentId: targetAgentId,
      }))
    );

    return newIntervals;
  }, [copiedIntervals]);

  // Clear clipboard
  const clearClipboard = useCallback(() => {
    setCopiedIntervals([]);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Undo: Ctrl+Z or Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      // Redo: Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || (e.ctrlKey && e.key === 'y')) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return {
    // History management
    addToHistory,
    undo,
    redo,
    canUndo: historyIndex >= 0,
    canRedo: historyIndex < history.length - 1,

    // Clipboard
    copyIntervals,
    pasteIntervals,
    clearClipboard,
    hasCopiedIntervals: copiedIntervals.length > 0,
    copiedIntervalsCount: copiedIntervals.length,
  };
}
